import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { allProducts } from "@/mocks/products";
import { useCart } from "@/hooks/useCart";
import PageHero from "@/components/feature/PageHero";
import CTASection from "@/components/feature/CTASection";
import ScrollReveal from "@/components/base/ScrollReveal";
import NutritionModal from "@/components/base/NutritionModal";
import type { Product } from "@/mocks/products";
import { motion } from "framer-motion";

const tabs = [
  { key: "all", label: "Todos" },
  { key: "sanduiches-naturais", label: "Sanduíches" },
  { key: "mini-pasteis", label: "Mini Pastéis" },
];

export default function CardapioPage() {
  const navigate = useNavigate();
  const { addItem, items, updateQuantity, openCart } = useCart();
  const [activeTab, setActiveTab] = useState("all");
  const [hoveredId, setHoveredId] = useState<string | null>(null);
  const [nutritionProduct, setNutritionProduct] = useState<Product | null>(null);

  const filtered =
    activeTab === "all"
      ? allProducts
      : allProducts.filter((p) => p.category === activeTab);

  const getCartQuantity = (productId: string) => {
    const item = items.find((i) => i.product.id === productId);
    return item ? item.quantity : 0;
  };

  return (
    <>
      <PageHero
        eyebrow="Cardápio Premium"
        title={
          <>
            Nossos produtos,<br />
            <em className="not-italic text-accent-400">frescos e artesanais.</em>
          </>
        }
        intro="Sanduíches naturais preparados diariamente e mini pastéis congelados com receitas exclusivas. Qualidade Big Mania em cada detalhe."
        image="https://storage.readdy-site.link/project_files/e2268d4a-73e3-4367-9d65-df24b53a0559/8c75bd71-e291-4776-8a10-8f345738612e_compressed_sanduche_em_embalagem_colorida_a.webp"
        imageAlt="Sanduíches naturais premium Big Mania"
        primaryLabel="Pedir pelo WhatsApp"
        primaryHref="https://wa.me/558534942701?text=Olá! Gostaria de fazer um pedido."
      />

      <section className="relative w-full py-16 md:py-24 bg-background-50 overflow-hidden">
        <div className="relative w-full px-4 sm:px-6 md:px-10 lg:px-16 max-w-7xl mx-auto">
          {/* Tabs */}
          <ScrollReveal className="flex justify-center mb-12">
            <div className="inline-flex p-1 rounded-full bg-background-200/70 border border-background-300/50">
              {tabs.map((tab) => (
                <button
                  key={tab.key}
                  onClick={() => setActiveTab(tab.key)}
                  className={`px-6 py-2.5 rounded-full text-sm font-semibold transition-all duration-300 cursor-pointer whitespace-nowrap ${
                    activeTab === tab.key
                      ? "bg-primary-500 text-background-50 shadow-md"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </ScrollReveal>

          {/* Illustrative photos note */}
          <ScrollReveal className="text-center mb-10">
            <p className="inline-flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 bg-background-100 border border-background-200/50 rounded-full px-4 py-1.5">
              <span className="w-4 h-4 flex items-center justify-center">
                <i className="ri-image-line text-primary-500"></i>
              </span>
              As fotos são meramente ilustrativas.
            </p>
          </ScrollReveal>

          {/* Product Grid */}
          <ScrollReveal
            direction="up"
            delay={0.1}
            staggerChildren
            staggerDelay={0.08}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5 md:gap-6">
              {filtered.map((product) => (
                <motion.div
                  key={product.id}
                  whileHover={{ y: -6 }}
                  transition={{ duration: 0.3 }}
                  onMouseEnter={() => setHoveredId(product.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  onClick={() => navigate(`/produto/${product.id}`)}
                  className="group relative flex flex-col rounded-2xl bg-background-50 border border-background-200/40 overflow-hidden cursor-pointer transition-all duration-500 hover:border-primary-300/50 hover:shadow-xl hover:shadow-primary-500/5"
                >
                  {/* Image */}
                  <div className="relative h-56 sm:h-60 overflow-hidden">
                    <img
                      src={product.image}
                      alt={product.name}
                      loading="lazy"
                      decoding="async"
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

                    {/* Badge */}
                    {product.badge && (
                      <span className="absolute top-3 left-3 px-2.5 py-1 rounded-md bg-primary-500 text-background-50 text-[10px] font-bold uppercase tracking-wider">
                        {product.badge}
                      </span>
                    )}

                    {/* Quick view overlay */}
                    <motion.div
                      initial={false}
                      animate={{
                        opacity: hoveredId === product.id ? 1 : 0,
                        y: hoveredId === product.id ? 0 : 10,
                      }}
                      transition={{ duration: 0.25 }}
                      className="absolute bottom-4 left-4 right-4 flex justify-center"
                    >
                      <span className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-background-50/95 backdrop-blur-sm text-sm font-semibold text-foreground-950 shadow-lg"
                      >
                        <i className="ri-eye-line"></i>
                        Ver detalhes
                      </span>
                    </motion.div>
                  </div>

                  {/* Content */}
                  <div className="flex flex-col flex-1 p-5">
                    <div className="flex items-center gap-2 mb-2">
                      <span className="inline-block px-2 py-0.5 rounded-md bg-primary-100 text-primary-700 text-[10px] font-semibold uppercase tracking-wider">
                        {product.category === "sanduiches-naturais"
                          ? "Sanduíche"
                          : "Mini Pastel"}
                      </span>
                      <span className="text-[10px] text-foreground-400">
                        {product.weight}
                      </span>
                    </div>

                    <h3 className="text-base font-heading font-semibold text-foreground-950 mb-1.5 group-hover:text-primary-600 transition-colors">
                      {product.name}
                    </h3>

                    <p className="text-sm text-foreground-500 leading-relaxed mb-4 flex-1 line-clamp-2">
                      {product.shortDescription}
                    </p>

                    {/* Tags */}
                    <div className="flex flex-wrap gap-1.5 mb-3">
                      {product.tags.slice(0, 2).map((tag) => (
                        <span
                          key={tag}
                          className="px-2 py-0.5 rounded-full bg-background-100 text-foreground-500 text-[10px] font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>

                    {product.detailedNutrition && (
                      <div className="mb-3">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setNutritionProduct(product);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-background-100 border border-background-200/50 text-xs text-foreground-600 hover:text-primary-600 hover:border-primary-300/50 transition-colors cursor-pointer whitespace-nowrap"
                        >
                          <span className="w-4 h-4 flex items-center justify-center">
                            <i className="ri-file-list-3-line"></i>
                          </span>
                          Informações Nutricionais
                        </button>
                      </div>
                    )}

                    {/* Footer */}
                    <div className="flex items-center justify-between pt-3 border-t border-background-200/50">
                      <div className="flex flex-col">
                        <span className="text-lg font-heading font-semibold text-primary-600">
                          R$ {product.price.toFixed(2)}
                        </span>
                        {product.category === "mini-pasteis" && (
                          <span className="text-[10px] text-foreground-400">
                            Caixa c/ 100 un
                          </span>
                        )}
                      </div>

                      {getCartQuantity(product.id) > 0 ? (
                        <div className="flex items-center gap-1.5">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              updateQuantity(product.id, getCartQuantity(product.id) - 1);
                            }}
                            className="w-7 h-7 flex items-center justify-center rounded-md border border-background-300 hover:bg-background-200 transition-colors cursor-pointer text-foreground-700"
                          >
                            <i className="ri-subtract-line text-sm"></i>
                          </button>
                          <span className="w-7 text-center text-sm font-semibold text-foreground-950">
                            {getCartQuantity(product.id)}
                          </span>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              updateQuantity(product.id, getCartQuantity(product.id) + 1);
                            }}
                            className="w-7 h-7 flex items-center justify-center rounded-md border border-background-300 hover:bg-background-200 transition-colors cursor-pointer text-foreground-700"
                          >
                            <i className="ri-add-line text-sm"></i>
                          </button>
                        </div>
                      ) : (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            addItem(product);
                          }}
                          className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-400 transition-all duration-300 cursor-pointer whitespace-nowrap"
                        >
                          <i className="ri-add-line"></i>
                          Adicionar
                        </button>
                      )}
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </ScrollReveal>
        </div>
      </section>

      {nutritionProduct && nutritionProduct.detailedNutrition && (
        <NutritionModal
          isOpen={true}
          onClose={() => setNutritionProduct(null)}
          data={nutritionProduct.detailedNutrition}
          productName={nutritionProduct.name}
        />
      )}

      <CTASection
        eyebrow="Faça seu pedido"
        subtitle="Oferecemos opções personalizadas para eventos, empresas e revenda. Entre em contato e descubra as possibilidades."
        primaryLabel="Pedir pelo WhatsApp"
        primaryHref="https://wa.me/558534942701?text=Olá! Gostaria de fazer um pedido."
      />
    </>
  );
}